import React, { useState, useRef, useEffect } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import ownersReset from "../services/owner-reset-password";
import "../styles/register.css";

const ResetPassword = () => {
  const location = useLocation();
  const resetId = location.state?.resetId;
  const [otp, setOtp] = useState(new Array(4).fill(""));

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [timer, setTimer] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [apiResponse, setApiResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const inputsRef = useRef([]);
  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validatePassword = (pwd) => pwd.length >= 8;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setOtpError("");
    setPasswordError("");
    setConfirmPasswordError("");
    setApiResponse("");

    if (!otp || otp.length !== 4 || otp.some((digit) => digit === "")) {
      setOtpError("Enter 4-digit OTP");
      return;
    }

    if (!password) {
      setPasswordError("Password is required.");
      return;
    }

    if (!validatePassword(password)) {
      setPasswordError("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);
      const otpNumber = Number(otp.join(""));

      const response = await ownersReset.resetPassword(
        resetId,
        otpNumber,
        password
      );
      setApiResponse(response.message || "Password reset successful!");
      setOtp(["", "", "", ""]);
      setPassword("");
      setConfirmPassword("");
    } catch (error) {
      setApiResponse(error.message || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (e, index) => {
    const value = e.target.value;
    if (/^\d$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      // Auto-focus next
      if (index < 3) {
        inputsRef.current[index + 1]?.focus();
      }
    }
  };
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index] === "") {
        if (index > 0) {
          inputsRef.current[index - 1]?.focus();
          const newOtp = [...otp];
          newOtp[index - 1] = "";
          setOtp(newOtp);
        }
      } else {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
    }
  };
  const handleResendOtp = async () => {
    setIsResending(true);
    try {
      const response = await ownersReset.resendOtp({ resetId });
      setApiResponse(response.message || "OTP resent successfully.");
      setTimer(60); // Restart timer
      setOtp(["", "", "", ""]); // Clear previous OTP input
      inputsRef.current[0]?.focus();
    } catch (error) {
      setApiResponse(error.message || "Failed to resend OTP.");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <section className="registration-section">
      <div className="container">
        <div className="login-form-wrap">
          {apiResponse && (
            <p
              className={`mt-3 ${
                apiResponse.toLowerCase().includes("error")
                  ? "alert alert-danger"
                  : "alert alert-success"
              }`}
            >
              {apiResponse}
            </p>
          )}

          <h2 className="form-title">Reset Your Password</h2>
          <p>Please enter the OTP and set a new password.</p>

          <Form onSubmit={handleSubmit}>
            <Row>
              <Col lg={12}>
                <div className="d-flex gap-2 mb-3">
                  {otp.map((digit, idx) => (
                    <input
                      key={idx}
                      type="text"
                      className="form-control text-center"
                      value={digit}
                      onChange={(e) => handleOtpChange(e, idx)}
                      onKeyDown={(e) => handleKeyDown(e, idx)}
                      ref={(el) => (inputsRef.current[idx] = el)}
                      maxLength={1}
                      style={{
                        width: "50px",
                        height: "50px",
                        fontSize: "24px",
                        borderRadius: "8px",
                      }}
                      inputMode="numeric"
                    />
                  ))}
                </div>
              </Col>
              <Col lg={12} className="mb-3 text-start">
                {timer > 0 ? (
                  <small className="text-muted">
                    Resend OTP in <strong>{timer}</strong> sec
                  </small>
                ) : (
                  <Button
                    variant="link"
                    className="p-0 text-decoration-underline"
                    onClick={handleResendOtp}
                    disabled={isResending}
                  >
                    {isResending ? "Resending..." : "Resend OTP"}
                  </Button>
                )}
              </Col>

              <Col lg={12}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    New Password<span className="imp-star">*</span>
                  </Form.Label>
                  <div className="password-input-container">
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      isInvalid={!!passwordError}
                    />
                    <span
                      className="password-toggle-icon"
                      onClick={togglePasswordVisibility}
                    >
                      {showPassword ? <FaEye /> : <FaEyeSlash />}
                    </span>
                    <Form.Control.Feedback type="invalid">
                      {passwordError}
                    </Form.Control.Feedback>
                  </div>
                </Form.Group>
              </Col>

              <Col lg={12}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Confirm Password<span className="imp-star">*</span>
                  </Form.Label>
                  <div className="password-input-container">
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      isInvalid={!!confirmPasswordError}
                    />
                    <span
                      className="password-toggle-icon"
                      onClick={togglePasswordVisibility}
                    >
                      {showPassword ? <FaEye /> : <FaEyeSlash />}
                    </span>
                    <Form.Control.Feedback type="invalid">
                      {confirmPasswordError}
                    </Form.Control.Feedback>
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Button
              type="submit"
              className="btn orange-btn w-100 button-register"
              disabled={loading}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </Form>
        </div>
      </div>
    </section>
  );
};

export default ResetPassword;
